// liveStore.js
// Single source of truth for Comprehend Live UI state: today's sessions,
// the action queue (patient-lifecycle worklist + long-run agent tasks),
// review items, and the chat transcript. Vanilla pub/sub so it can be used
// from React (useComprehendLive) and from agent tool handlers alike.
import { Emitter } from './events';
import { buildScheduleFromSessions } from './scheduleBuild';
import { parseBackendDateMs } from './time';
import { scoreToGrade } from 'components/Misc/complianceGrades';

export const QUEUE_KINDS = {
  NUDGE: 'nudge', // in-visit: doc gaps + clinical decision support
  ACTION: 'action', // lifecycle worklist + long-run agent tasks
  REVIEW: 'review', // finished notes awaiting review & sign
};

let nextLocalId = 1;
const localId = (prefix) => `${prefix}_${nextLocalId++}`;
const cleanName = (value) => String(value || '').trim();
const isUsefulPatientName = (name) => {
  const text = cleanName(name).toLowerCase();
  return text && text !== 'unknown' && text !== 'none';
};
const isGenericConversationName = (name) => {
  const text = cleanName(name).toLowerCase();
  return !text || text === 'conversation' || text === 'conversation name';
};

const VOICE_REPLIES_KEY = 'comprehendpt_live_voiceReplies';
const VERBOSITY_KEY = 'comprehendpt_live_verbosity';
const MODE_KEY = 'comprehendpt_live_mode';
const MIC_KEY = 'comprehendpt_selectedMicId'; // shared with the recorder
const BUFFER_BEFORE_KEY = 'comprehendpt_live_bufferBefore';
const BUFFER_AFTER_KEY = 'comprehendpt_live_bufferAfter';
const AUTO_SWITCH_KEY = 'comprehendpt_live_autoSwitch';
// Passive listening is OPT-IN and sticky. Nothing opens the mic on load unless
// the clinician turned it on before — a page load in a room full of patients
// must not start recording on its own.
const AUTO_LISTEN_KEY = 'comprehendpt_live_autoListen';
const VISIT_TIMES_KEY = 'comprehendpt_live_visitTimes'; // {conversationId: ms}

const readJSON = (key, fallback) => {
  try {
    const v = JSON.parse(localStorage.getItem(key));
    return v && typeof v === 'object' ? v : fallback;
  } catch (e) {
    return fallback;
  }
};

const readNum = (key, fallback) => {
  try {
    const v = parseFloat(localStorage.getItem(key));
    return Number.isFinite(v) ? v : fallback;
  } catch (e) {
    return fallback;
  }
};

const readLocal = (key, fallback) => {
  try {
    return localStorage.getItem(key) || fallback;
  } catch (e) {
    return fallback;
  }
};

export const persistLocal = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // storage unavailable
  }
};

export const SETTINGS_KEYS = {
  VERBOSITY_KEY,
  MODE_KEY,
  MIC_KEY,
  AUTO_LISTEN_KEY,
};

function readVoiceRepliesPref() {
  try {
    return sessionStorage.getItem(VOICE_REPLIES_KEY) === '1';
  } catch (e) {
    return false;
  }
}

export const persistVoiceRepliesPref = (enabled) => {
  try {
    sessionStorage.setItem(VOICE_REPLIES_KEY, enabled ? '1' : '0');
  } catch (e) {
    // storage unavailable — preference just won't survive reload
  }
};

const initialState = () => ({
  expanded: false,
  // Which experience the tray renders: 'ambient' (full Comprehend Live) or
  // 'multirecord' (V0.5 MultiRecord++ — vertical timeline, auto-switcher,
  // schedule loader, no ambient brain). Set from feature flags on mount.
  trayMode: 'ambient',
  reviewMode: false,
  drillInConversationId: null,
  loading: false,
  hydratedAt: null,
  demo: false,
  // True while the classic 2-min recorder (web app MultiRecorder) is the
  // system of record — the ambient pipeline then listens/acts but must NOT
  // also commit transcripts (double-commit guard).
  classicRecorderActive: false,
  // MultiRecord++ (V0.5): the day is "on" (mic consented, auto-switcher
  // running) and the clinician's manual record overrides on top of the
  // schedule-computed set.
  recordingDay: false,
  manualOverrides: { manualOn: [], manualOff: [] },
  // Customizable record window (minutes): lead-in before a visit's scheduled
  // start, and how long to keep recording past its scheduled end (handoff).
  bufferBeforeMin: readNum(BUFFER_BEFORE_KEY, 2),
  bufferAfterMin: readNum(BUFFER_AFTER_KEY, 5),
  // Auto-record on schedule: OFF by default — recording follows the schedule
  // clock (within the buffer window) only when the clinician turns it on;
  // otherwise it's manual (tap a visit to record it).
  autoSwitch: readLocal(AUTO_SWITCH_KEY, '0') === '1',
  // Per-visit time anchors (conversationId -> {start,end}). Local optimistic
  // layer + the only store on a backend without scheduled-time fields. When the
  // backend has them (scheduledTimesSupported), it's authoritative and these
  // are just instant feedback until the next hydrate. Persisted across reloads.
  visitTimeOverrides: readJSON(VISIT_TIMES_KEY, {}),
  // Set on hydrate: true once the backend returns scheduledStart/End, which
  // enables persisting drags/creates server-side (syncs web <-> phone).
  scheduledTimesSupported: false,
  // Live mic RMS (0..~0.1) from whichever recorder runtime is active —
  // drives the recording wave in the rail/banner so the clinician can see
  // it's hearing even when the tray is collapsed.
  micLevel: 0,
  // Ambient-only demo toggle: which view the tray shows — 'agent' (queue +
  // chat) or 'timeline' (the vertical MultiRecord++ view). Ambient users get
  // BOTH and can flip; multirecord-mode users always see the timeline.
  panelView: 'agent',
  ambientStatus: 'off', // 'off' | 'listening' | 'paused'
  ambientPausedReason: null, // 'cap' | 'idle' when paused (demo safety limits)
  // True while the agent is acting on an ambient (overheard) instruction —
  // drives the screen aura so the clinician sees it working even when the
  // mic isn't open for an explicit wake-word turn.
  agentActing: false,
  // True from a ⚡ Catch-up tap until the forced chunk's round-trip resolves
  // — drives the bolt's working animation so the tap has feedback.
  ambientCatchingUp: false,
  // Hard mic kill: ambient stops, wake word disarms, the realtime session
  // is destroyed (its prewarmed stream otherwise keeps Chrome's mic
  // indicator lit even while idle). The panel keeps working for typing.
  micKilled: false,
  micMuted: false,
  // Opt-in, persisted master switch for passive listening: gates the wake
  // word, the realtime pre-warm, and the ambient brain. OFF by default, so a
  // fresh page load never opens the mic on its own. Distinct from micKilled /
  // micMuted, which are session-scoped panic/pause controls.
  autoListen: readLocal(AUTO_LISTEN_KEY, '0') === '1',

  // Slot-based schedule the Pulse runs on.
  // [{slotId, start: Date, end: Date,
  //   patients: [{patientName, group, conversationId}]}]
  schedule: [],

  // [{conversationId, name, patientName, group, status, createdAt,
  //   recording, elapsedSeconds, score, grade, nudges: [queueItemId...]}]
  sessions: [],

  // [{id, kind, severity, conversationId, patientName, title, detail,
  //   proposedText, status: 'pending'|'working'|'resolved'|'dismissed',
  //   progress, undo: {fieldName, previous}, source}]
  queue: [],

  // Chat doubles as the agent's activity ledger:
  // [{id, role: 'user'|'assistant'|'receipt', text, pending, why,
  //   undoable, undone, action: {label, type}}]
  chat: [],

  // Text-only by default; opt into spoken replies per browser session.
  voiceReplies: readVoiceRepliesPref(),
  settingsOpen: false,
  agentVerbosity: readLocal(VERBOSITY_KEY, 'normal'),
  agentMode: readLocal(MODE_KEY, 'clinic'), // clinic | conversation
  micId: readLocal(MIC_KEY, ''),
  // Conversation layer: chat is just the bottom bar until engaged (wake
  // word / typing / mic); then it overlays the queue and auto-dismisses
  // after inactivity (see ChatOverlay).
  chatOverlay: false,
  chatOverlayMode: 'peek', // 'peek' = this turn only; 'full' = history
  chatTurnStartIndex: 0, // chat index where the current engagement began
  queueSpotlightAt: 0, // chat ducks briefly after this so actions are visible
  queueFocusItemId: null, // card to scroll into view when acted on
  queueFocusAt: 0,
  chatLastActivityAt: 0,
  awaitingReply: false,
  agentStatus: 'idle',
  agentSpeaking: false,
  wakeArmed: false,
  anyRecording: false,
  dictateTargetId: null,
});

// The shared scale (components/Misc/complianceGrades.js); re-exported for
// the agent modules that import it from here.
export { scoreToGrade };

export class LiveStore {
  constructor() {
    this.state = initialState();
    this.events = new Emitter();
    // Undo handlers for receipts (functions can't live in state).
    this.undoHandlers = new Map();
  }

  getState() {
    return this.state;
  }

  subscribe(cb) {
    return this.events.on('change', cb);
  }

  setState(partial) {
    this.state = { ...this.state, ...partial };
    this.events.emit('change', this.state);
  }

  /* ===== Panel state ===== */

  setExpanded(expanded) {
    this.setState({ expanded: Boolean(expanded) });
  }

  openDrillIn(conversationId) {
    this.setState({
      expanded: true,
      reviewMode: false,
      drillInConversationId: conversationId,
    });
  }

  closeDrillIn() {
    this.setState({ drillInConversationId: null });
  }

  setReviewMode(on) {
    this.setState({
      reviewMode: Boolean(on),
      drillInConversationId: null,
      expanded: this.state.expanded || Boolean(on),
    });
  }

  /* ===== Chat ===== */

  addChatMessage(role, text, { pending = false } = {}) {
    const message = { id: localId('msg'), role, text, pending };
    this.setState({ chat: [...this.state.chat, message] });
    return message.id;
  }

  updateChatMessage(id, text, { pending = false } = {}) {
    this.setState({
      chat: this.state.chat.map((m) =>
        m.id === id ? { ...m, text, pending } : m,
      ),
    });
  }

  /* ===== Receipts (the agent's activity ledger, lives in the chat) ===== */

  /**
   * Post an action receipt into the chat stream.
   * @param {Object} r
   * @param {string} r.text - what happened ("Switched recording to Jimmy")
   * @param {string} [r.why] - the trigger/reason, shown on hover
   * @param {Function} [r.onUndo] - makes the receipt undoable
   * @param {{label: string, type: string}} [r.action] - optional CTA
   *   (e.g. {label: 'Review', type: 'review'})
   */
  addReceipt({ text, why, onUndo, action }) {
    const message = {
      id: localId('msg'),
      role: 'receipt',
      text,
      why: why || null,
      undoable: typeof onUndo === 'function',
      undone: false,
      action: action || null,
    };
    if (message.undoable) this.undoHandlers.set(message.id, onUndo);
    this.setState({ chat: [...this.state.chat, message] });
    return message.id;
  }

  async undoReceipt(messageId) {
    const handler = this.undoHandlers.get(messageId);
    if (!handler) return;
    this.undoHandlers.delete(messageId);
    try {
      await handler();
    } finally {
      this.setState({
        chat: this.state.chat.map((m) =>
          m.id === messageId ? { ...m, undone: true, undoable: false } : m,
        ),
      });
    }
  }

  /* ===== Schedule (Pulse input) ===== */

  setSchedule(slots) {
    this.setState({ schedule: slots || [] });
  }

  setDemo(on) {
    this.setState({ demo: Boolean(on) });
  }

  /* ===== Sessions ===== */

  upsertSession(session) {
    const sessions = [...this.state.sessions];
    const idx = sessions.findIndex(
      (s) => s.conversationId === session.conversationId,
    );
    if (idx >= 0) sessions[idx] = { ...sessions[idx], ...session };
    else sessions.push(session);
    this.setState({ sessions });
  }

  setRecordingSessions(conversationIds) {
    // computeActiveSet returns STRING ids; session ids may be numbers. Compare
    // as strings both ways or the rail's recording flags silently drift from
    // the timeline's active set (rail showed only the current slot, not the
    // full buffered window).
    const recordingSet = new Set((conversationIds || []).map(String));
    const sessions = this.state.sessions.map((s) => ({
      ...s,
      recording: recordingSet.has(String(s.conversationId)),
    }));
    this.setState({
      sessions,
      anyRecording: recordingSet.size > 0,
      // This setter is the MultiRecorder sync path — recording driven by
      // the classic recorder, so ambient must not double-commit.
      classicRecorderActive: recordingSet.size > 0,
    });
  }

  setDictateTarget(conversationId) {
    this.setState({ dictateTargetId: conversationId });
  }

  /* ===== MultiRecord++ (V0.5) ===== */

  setMicLevel(level) {
    // Cheap, high-frequency — guard against redundant renders.
    const next = Number(level) || 0;
    if (Math.abs(next - this.state.micLevel) > 0.002) {
      this.setState({ micLevel: next });
    }
  }

  setPanelView(view) {
    this.setState({ panelView: view === 'timeline' ? 'timeline' : 'agent' });
  }

  togglePanelView() {
    this.setState({
      panelView: this.state.panelView === 'timeline' ? 'agent' : 'timeline',
    });
  }

  setRecordingDay(on) {
    this.setState({ recordingDay: Boolean(on) });
    // Note: do NOT clear manualOverrides here — this is also called from the
    // runtime's mic-failure path, and wiping the clinician's tapped intent on
    // a transient mic error is wrong (the card should stay lit). Explicit
    // "Stop recording" clears overrides via clearManualRecording().
    if (!on) this.setRecordingSessions([]);
  }

  clearManualRecording() {
    this.setState({ manualOverrides: { manualOn: [], manualOff: [] } });
  }

  setRecordBuffers(beforeMin, afterMin) {
    const before = Math.max(0, Number(beforeMin) || 0);
    const after = Math.max(0, Number(afterMin) || 0);
    this.setState({ bufferBeforeMin: before, bufferAfterMin: after });
    persistLocal(BUFFER_BEFORE_KEY, String(before));
    persistLocal(BUFFER_AFTER_KEY, String(after));
  }

  setAutoSwitch(on) {
    const v = Boolean(on);
    this.setState({ autoSwitch: v });
    persistLocal(AUTO_SWITCH_KEY, v ? '1' : '0');
  }

  // Pin a visit to a specific time window {start, end} (e.g. drawn by
  // dragging an empty span, or moved by the card grip). Rebuilds the schedule
  // immediately so the card jumps there. endMs optional (omit to keep the
  // inferred duration).
  setVisitWindow(conversationId, startMs, endMs) {
    if (!Number.isFinite(startMs)) return;
    const id = String(conversationId);
    const end = Number.isFinite(endMs) && endMs > startMs ? endMs : null;
    const win = end ? { start: startMs, end } : { start: startMs };
    const overrides = { ...this.state.visitTimeOverrides, [id]: win };
    persistLocal(VISIT_TIMES_KEY, JSON.stringify(overrides));
    // Optimistically patch the in-memory session too, so a re-drag of a visit
    // that already has a backend scheduledStart moves instantly (backend value
    // would otherwise win until the next hydrate). Hydrate reconciles.
    const sessions = this.state.sessions.map((s) =>
      String(s.conversationId) === id
        ? { ...s, scheduledStart: startMs, scheduledEnd: end }
        : s,
    );
    this.setState({
      sessions,
      visitTimeOverrides: overrides,
      schedule: buildScheduleFromSessions(sessions, overrides),
    });
  }

  /** Explicitly force a visit's record state on/off (manual override on top
   * of the schedule). on -> manualOn (force record); off while the schedule
   * wants it -> manualOff (force stop). Off before the auto-window just clears
   * manualOn so the visit can still start on schedule later. */
  setManualRecord(conversationId, on, scheduleWantsIt = false) {
    const id = String(conversationId);
    const onSet = new Set(this.state.manualOverrides.manualOn.map(String));
    const offSet = new Set(this.state.manualOverrides.manualOff.map(String));
    onSet.delete(id);
    offSet.delete(id);
    if (on) onSet.add(id);
    else if (scheduleWantsIt) offSet.add(id);
    this.setState({
      manualOverrides: { manualOn: [...onSet], manualOff: [...offSet] },
    });
  }

  /** Toggle a manual record override for one visit, relative to whatever
   * the schedule currently wants. If the schedule already has it on, a tap
   * forces it OFF; if off, a tap forces it ON. */
  toggleManualRecord(conversationId, scheduleWantsIt) {
    const id = String(conversationId);
    const { manualOn, manualOff } = this.state.manualOverrides;
    const on = new Set(manualOn.map(String));
    const off = new Set(manualOff.map(String));
    // Clear any existing override for this id first.
    const wasOn = on.has(id);
    const wasOff = off.has(id);
    on.delete(id);
    off.delete(id);
    // Effective current state = schedule, unless an override flipped it.
    const effective = wasOn ? true : wasOff ? false : scheduleWantsIt;
    if (effective) off.add(id);
    else on.add(id);
    this.setState({
      manualOverrides: { manualOn: [...on], manualOff: [...off] },
    });
  }

  setMicKilled(killed) {
    this.setState({ micKilled: Boolean(killed) });
  }

  // Soft mute: stop passive listening (ambient + wake word) without the hard
  // teardown of the realtime session that micKilled does. For demos.
  setMicMuted(muted) {
    this.setState({ micMuted: Boolean(muted) });
  }

  /** Opt into (or out of) passive listening. Persisted, so the choice carries
   * across reloads — turning it on is also what makes it auto-start next time.
   * Turning it OFF clears the session-scoped mute so the next opt-in isn't
   * silently swallowed by a stale mute from a previous session. */
  setAutoListen(on) {
    const next = Boolean(on);
    this.setState({ autoListen: next, ...(next ? {} : { micMuted: false }) });
    persistLocal(AUTO_LISTEN_KEY, next ? '1' : '0');
  }

  /* ===== Chat overlay (the conversation layer) ===== */

  openChatOverlay() {
    const fresh = !this.state.chatOverlay;
    this.setState({
      chatOverlay: true,
      // A fresh engagement starts a new turn window (this conversation
      // only); re-engaging mid-turn keeps whatever the user expanded.
      chatOverlayMode: fresh ? 'peek' : this.state.chatOverlayMode,
      chatTurnStartIndex: fresh
        ? this.state.chat.length
        : this.state.chatTurnStartIndex,
      chatLastActivityAt: Date.now(),
      expanded: true,
    });
  }

  setChatOverlayMode(mode) {
    this.setState({
      chatOverlayMode: mode === 'full' ? 'full' : 'peek',
      chatLastActivityAt: Date.now(),
    });
  }

  bumpChatActivity() {
    if (this.state.chatOverlay) {
      this.setState({ chatLastActivityAt: Date.now() });
    }
  }

  closeChatOverlay() {
    this.setState({ chatOverlay: false });
  }

  /** Reopen the conversation from the ghost trace. Windowed to the most
   * recent exchange (last user message onward) in the normal bottom-up
   * peek layer — jumping straight into the full-history takeover was
   * jarring and landed scrolled to the TOP of the transcript. Scrolling
   * up inside still expands to history. (A fresh openChatOverlay would
   * reset the turn window to empty and render nothing.) */
  reopenChat() {
    const chat = this.state.chat;
    let start = -1;
    for (let i = chat.length - 1; i >= 0; i -= 1) {
      if (chat[i].role === 'user') {
        start = i;
        break;
      }
    }
    if (start < 0) start = Math.max(0, chat.length - 5);
    this.setState({
      chatOverlay: true,
      chatOverlayMode: 'peek',
      chatTurnStartIndex: start,
      chatLastActivityAt: Date.now(),
      expanded: true,
    });
  }

  /** Briefly duck the chat layer so a queue action is visible as it
   * animates (only matters while the overlay is up). */
  pingQueueSpotlight(itemId = null) {
    const ducked = this.state.chatOverlay;
    this.setState({
      // The queue scrolls this card into view before the choreography.
      queueFocusItemId: itemId,
      queueFocusAt: Date.now(),
      ...(ducked
        ? {
            queueSpotlightAt: Date.now(),
            // Hold the overlay's dismiss countdown through the choreography.
            chatLastActivityAt: Date.now(),
          }
        : {}),
    });
    return ducked;
  }

  /* ===== Queue ===== */

  addQueueItem(item) {
    const full = {
      id: item.id || localId('q'),
      status: 'pending',
      severity: 'info',
      ...item,
    };
    this.setState({ queue: [...this.state.queue, full] });
    return full.id;
  }

  updateQueueItem(id, partial) {
    this.setState({
      queue: this.state.queue.map((q) =>
        q.id === id ? { ...q, ...partial } : q,
      ),
    });
  }

  findQueueItem(predicate) {
    return this.state.queue.find(predicate);
  }

  /** Long-run agent tasks render as ACTION items with working/progress state. */
  addTask(title, { conversationId, patientName } = {}) {
    return this.addQueueItem({
      kind: QUEUE_KINDS.ACTION,
      source: 'agent',
      title,
      conversationId,
      patientName,
      status: 'working',
      progress: 0,
    });
  }

  resolveTask(id, detail) {
    this.updateQueueItem(id, { status: 'resolved', progress: 100, detail });
  }

  failTask(id, detail) {
    this.updateQueueItem(id, { status: 'failed', detail, severity: 'urgent' });
  }

  /* ===== Hydration ===== */

  /**
   * Pull today's working set. Prefers the backend queue endpoint
   * (/v1/agent/queue); falls back to assembling client-side from recent
   * conversations + their compliance evaluations.
   */
  async hydrate(apiClient) {
    if (this.state.demo) return; // demo feeder owns all data
    if (this.state.loading) return;
    this.setState({ loading: true });
    try {
      // MultiRecord++ lets you step the timeline back AND forward through days,
      // so it needs more than today's working set: pull a month back so
      // stepping back shows real visits, and forward through the rest of today
      // + the next 2 weeks so future-scheduled visits load too (most views
      // default to "up to now" and would clip the future). Ambient just wants
      // today (+ yesterday for queued results).
      const multirecord = this.state.trayMode === 'multirecord';
      const lookbackDays = multirecord ? 30 : 1;
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      start.setDate(start.getDate() - lookbackDays);
      const end = new Date();
      if (multirecord) {
        end.setDate(end.getDate() + 14);
        end.setHours(23, 59, 59, 999);
      }

      // MultiRecord++ has no agent queue/worklist — it just needs the
      // conversations for the timeline. Skip the queue endpoint (and the
      // per-conversation fallback below) entirely in that mode.
      let serverQueue = null;
      if (!multirecord && typeof apiClient.getAgentQueue === 'function') {
        try {
          const rsp = await apiClient.getAgentQueue();
          if (rsp && !rsp.error && Array.isArray(rsp.items)) serverQueue = rsp;
        } catch (err) {
          console.warn('Agent queue endpoint unavailable, using fallback', err);
        }
      }

      const data = await apiClient.getUserConversations(
        null,
        start.toISOString(),
        end.toISOString(),
      );
      const conversations =
        data && !data.error && data.conversations ? data.conversations : [];

      const today = new Date();
      const sessions = conversations
        .slice()
        .sort(
          (a, b) =>
            (parseBackendDateMs(a.createdAt) || 0) -
            (parseBackendDateMs(b.createdAt) || 0),
        )
        .map((c) => {
          const createdMs = parseBackendDateMs(c.createdAt);
          const created = new Date(createdMs);
          const isToday = created.toDateString() === today.toDateString();
          const conversationName = cleanName(c.conversationName);
          const patientName = isUsefulPatientName(c.patientName)
            ? cleanName(c.patientName)
            : cleanName(conversationName.split(' - ')[0]) || 'Untitled visit';
          const name = isGenericConversationName(conversationName)
            ? patientName
            : conversationName;
          return {
            conversationId: c.conversationId,
            name,
            patientName,
            group: c.group,
            createdAt: c.createdAt,
            createdAtMs: createdMs,
            isToday,
            status: c.status,
            // For the lifecycle status dot (no audio / needs comprehend / done).
            secondsTranscribed: c.secondsTranscribed,
            // Scheduled visit window from the backend (epoch ms) — the timeline
            // anchors on these when present (else falls back to createdAt /
            // local override). undefined on a backend that doesn't return them.
            scheduledStart: c.scheduledStart,
            scheduledEnd: c.scheduledEnd,
            recording: false,
            score: null,
            grade: '--',
          };
        });

      let queue = this.state.queue.filter((q) => q.source === 'agent'); // keep live tasks
      if (serverQueue) {
        queue = [...queue, ...serverQueue.items];
        if (Array.isArray(serverQueue.sessions)) {
          // Backend can enrich sessions (scores, lifecycle) when available.
          serverQueue.sessions.forEach((enriched) => {
            const s = sessions.find(
              (x) => x.conversationId === enriched.conversationId,
            );
            if (s) Object.assign(s, enriched);
          });
        }
      } else if (!multirecord) {
        queue = [
          ...queue,
          ...(await this.buildFallbackQueue(apiClient, sessions)),
        ];
      }

      // Does this backend return the scheduled-time fields? (Key present even
      // when null.) If so, we can persist drags/creates server-side; if not,
      // we stay on the local-only override and DON'T write (avoids polluting an
      // older backend's edit handler).
      const scheduledTimesSupported = conversations.some(
        (c) => c && Object.prototype.hasOwnProperty.call(c, 'scheduledStart'),
      );

      this.setState({
        sessions,
        queue,
        scheduledTimesSupported,
        // The day's timeline is derived from the conversations: anchored at the
        // backend's scheduledStart when present, else a local pinned override,
        // else createdAt. Source of truth on every hydrate (survives reload).
        schedule: buildScheduleFromSessions(
          sessions,
          this.state.visitTimeOverrides,
        ),
        loading: false,
        hydratedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error('ComprehendLive hydrate failed:', error);
      this.setState({ loading: false });
    }
  }

  /**
   * Client-side queue assembly used until /v1/agent/queue ships everywhere:
   * compliance gaps become nudges/reviews from each scored conversation.
   */
  async buildFallbackQueue(apiClient, sessions) {
    const queue = [];
    const scored = sessions.slice(-8); // cap detail fetches
    await Promise.all(
      scored.map(async (session) => {
        try {
          const details = await apiClient.getConversationDetails(
            session.conversationId,
          );
          if (!details || details.error) return;
          session.score = details.score ?? null;
          session.grade = scoreToGrade(details.score);

          const evals = details.requirement_evaluations || [];
          evals
            .filter((e) => e.status && !['fully met', 'not applicable'].includes(e.status))
            .slice(0, 3)
            .forEach((e) => {
              queue.push({
                id: localId('q'),
                kind: QUEUE_KINDS.NUDGE,
                source: 'compliance',
                severity: e.is_required ? 'urgent' : 'warn',
                conversationId: session.conversationId,
                patientName: session.patientName,
                title: e.requirement_text,
                detail: e.status,
                proposedText: e.suggestion || null,
                status: 'pending',
              });
            });

          const isFinished = (details.status || '').includes(
            'Transcription Complete',
          );
          if (isFinished) {
            queue.push({
              id: localId('q'),
              kind: QUEUE_KINDS.REVIEW,
              source: 'compliance',
              severity:
                details.score !== null && details.score < 60
                  ? 'urgent'
                  : 'info',
              conversationId: session.conversationId,
              patientName: session.patientName,
              title: `${session.patientName} — note ready to review`,
              detail: `Grade ${scoreToGrade(details.score)}${
                details.score ? ` (${details.score})` : ''
              }`,
              status: 'pending',
            });
          }
        } catch (err) {
          // Non-fatal: a single conversation failing shouldn't kill the queue.
          console.warn(
            'queue assembly skipped conversation',
            session.conversationId,
            err,
          );
        }
      }),
    );
    return queue;
  }

  /** Snapshot pushed into the agent's ContextRegistry so cards are voice-addressable. */
  getAgentContextSnapshot() {
    return {
      sessions: this.state.sessions.map((s) => ({
        conversationId: s.conversationId,
        patientName: s.patientName,
        recording: s.recording,
        grade: s.grade,
        status: s.status,
      })),
      queue: this.state.queue
        .filter((q) => q.status === 'pending' || q.status === 'working')
        .map((q) => ({
          id: q.id,
          kind: q.kind,
          patientName: q.patientName,
          title: q.title,
          proposedText: q.proposedText,
          status: q.status,
        })),
      dictateTargetId: this.state.dictateTargetId,
      anyRecording: this.state.anyRecording,
    };
  }
}

/* ===== Agent engagement predicates =====
 * These two used to be written inline in two files and drifted: suppression
 * keyed off chatOverlay, the aura didn't, so the ambient brain stayed deaf for
 * 10-20s AFTER the surround faded (the overlay's dismissal clock is HOLD_MS +
 * a reply-length drain + EXIT_MS). Keep them adjacent so any future change has
 * to consider both. */

/** The realtime agent owns the mic or the speakers — ambient must stand down so
 * it neither double-acts on the command nor transcribes the agent's own voice
 * back into the chart.
 *
 * Deliberately NOT chatOverlay: a transcript lingering on screen is a reading
 * affordance, not a reason to go deaf. Deliberately NOT agentActing: ambient
 * should keep listening while an overheard instruction is carried out. */
export const agentHoldsAudio = (s) =>
  s.agentStatus === 'listening' ||
  s.agentStatus === 'connecting' ||
  s.agentSpeaking;

/** The agent is visibly engaged — drives the full-viewport listening aura.
 * Includes everything in agentHoldsAudio so the aura is a truthful read on
 * "the brain has stood down": if the glow is up, ambient is not listening.
 * 'connecting' is in here for that reason — the mic is already handed over
 * during session setup, so leaving it dark meant a deaf gap with no cue.
 *
 * The one exception is agentActing: the glow marks the agent working an
 * instruction it overheard, and ambient deliberately KEEPS listening then. */
export const agentVisiblyEngaged = (s) =>
  agentHoldsAudio(s) || s.agentActing;

export const liveStore = new LiveStore();
export default liveStore;
